import { customElement, html, classMap } from '@adobe/edex/ui/edex-lit';
import { $t, getTranslation } from '@adobe/edex/ui/shared/config/i18n';
import { NX_SCHOOL_DISTRICT } from '@env/environment';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-visibility';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-share-light';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-bookmark-single-outline';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-alert';

import {
  DialogState,
  EntityStatus,
  ExportType,
  FileSchema,
  MarshalledResource,
  ShareButtonMeta,
  SocialNetwork,
} from '@adobe/edex/ui/types';
import * as routines from '@adobe/edex/ui/store/routines';
import {
  EdexDomID,
  EdexTagName,
  DEFAULT_RESOURCE_IMAGE,
  AdobeAnalyticsEvent,
  FsBookmarkAction,
  DialogHeadingClassName,
  SURVEY_CONTENT_ACQUIRED_COOKIE_KEY,
} from '@adobe/edex/ui/shared/constants';
import { selectDialog, selectResource } from '@adobe/edex/ui/store/selectors';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { BaseAuthElement } from '@adobe/edex/ui/components/base.element';
import '@adobe/edex/ui/shared/components/dialog-next/dialog';
import '@adobe/edex/ui/shared/components/school-attribution-dialog/school-attribution-dialog';
import { AnalyticsEvent } from '@adobe/edex/ui/constants';
import {
  getAssetURL,
  isSmallerMobileScreen,
  trackTargetEvent,
  setSurveyCookie,
  isSchoolDistrictAttributionDisabled,
} from '@adobe/edex/ui/shared/utils';
import { Analytics } from '@adobe/edex/ui/shared/config/analytics';

import '@adobe/edex/ui/components/resource/settings-dialog/settings-dialog';
import '@adobe/edex/ui/shared/components/header/search/signUpDialog/signUp-dialog';
import { FullStoryUtils } from '@adobe/edex/ui/shared/config/fullstory';
import { DownloadPayLoad } from '@adobe/edex/ui/types/payload';
import { EdexAuth } from '@adobe/edex/ui/shared/config/auth';
import { errorToast } from '@adobe/edex/ui/utils/notification';

import(
  /* webpackChunkName: "component/resource-toolbar-element-styles" */
  /* webpackPreload: true */
  /* webpackMode: "lazy" */
  '@adobe/edex/ui/components/resource/resource-header-toolbar/resource-header-toolbar.scss'
);

interface ToolbarItem {
  id: EdexDomID;
  tooltip: I18nKey;
  additionalToolTip?: string;
  label?: string;
  icon: any;
  clicked?: boolean;
  onClick?: (e: any) => any;
  show?: boolean;
}
@customElement(EdexTagName.resourceHeaderToolbar)
export class ResourceHeaderToolbar extends BaseAuthElement {
  get useLightDom() {
    return true;
  }

  static get properties() {
    return {
      ...super.properties,
      renderDialogs: { type: Boolean },
      resource: { attribute: false },
      dialog: { attribute: false },
      locationSearch: { type: String },
      isMobileView: { attribute: false },
      saveDialogOpen: { type: Boolean },
      isMobileUserAgent: { attribute: false },
      openSignUpDialog: { attribute: false },
      downloadResourceDialog: { attribute: null },
    };
  }
  renderDialogs: boolean;
  resource: MarshalledResource;
  dialog: DialogState;
  locationSearch: string;
  isMobileView = false;
  saveDialogOpen = false;
  isMobileUserAgent = false;
  openSignUpDialog = false;
  downloadResourceDialog = null;

  actions = {
    deleteResource: routines.deleteResource.trigger,
    updateResource: routines.updateResource.trigger,
    reportResource: routines.reportResource.trigger,
    unReportResource: routines.unReportResource.trigger,
    openDialog: routines.openDialog.trigger,
    closeDialog: routines.closeDialog.trigger,
    saveResource: routines.saveResource.trigger,
    unSaveResource: routines.unSaveResource.trigger,
  };

  stateChanged(state) {
    super.stateChanged(state);
    this.resource = selectResource(state);
    this.dialog = selectDialog(state);
  }

  isDialogOpen(id: EdexDomID) {
    return this.renderDialogs && this.dialog?.id === id;
  }

  getDialogOpener = (id: EdexDomID) => () => {
    this.actions.openDialog({ id });
  };

  get canEdit(): boolean {
    return this.isOwner(this.resource) || this.isAdmin;
  }

  get canReport(): boolean {
    const { isGuest, isOwner } = this;
    return Boolean(!isGuest && !isOwner(this.resource));
  }

  get isPublished() {
    return this.resource?.status === EntityStatus.active;
  }

  get isSchoolDistrictAttributionSessionDisabled() {
    return isSchoolDistrictAttributionDisabled();
  }

  isSchoolDistrictDialogNotApplicable() {
    const { isSchoolDistrictAttributionSessionDisabled, hasSchoolDistrictInfo } = this;
    if (NX_SCHOOL_DISTRICT === 'true') {
      return isSchoolDistrictAttributionSessionDisabled || hasSchoolDistrictInfo;
    } else {
      return true;
    }
  }

  handleReport() {
    const { resource, actions } = this;
    (resource.flagged ? actions.unReportResource : actions.reportResource)({
      id: resource.id,
    });
    this.actions.closeDialog();
  }

  emitFSEvent(eventName: string, payload: { [key: string]: any }) {
    FullStoryUtils.getInstance().event(eventName, {
      resourceID: this.resource.id,
      vanityURL: this.resource.vanityURL,
      ...payload,
    });
  }

  handleShare(event, { shareUrl, name }: ShareButtonMeta) {
    event.preventDefault();

    this.track({
      event: AnalyticsEvent.resourceClickShare,
      payload: { id: this.resource.id, socialNetworkID: name.toLowerCase() },
    });

    Analytics.getInstance().emitEvent(AdobeAnalyticsEvent.resourceShare, 'Resource - Share');
    window.open(
      shareUrl,
      'targetWindow',
      'toolbar=no,location=0,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=350'
    );
  }

  cancelSendToDrive = () => {
    const {
      dialog: {
        payload: { source },
      },
    } = this;
    this.actions.closeDialog();
    source.cancel();
  };

  /**
   * use Web Share API for native share
   * Track events:
   * * successful click event and,
   * * user aborted native share events
   */
  async nativeShare() {
    try {
      await navigator.share({
        title: this.resource?.title,
        text: $t(I18nKey.resourceHeaderNativeShareText),
        url: window.location.href,
      });
      this.track({
        event: AnalyticsEvent.resourceSaveForLater,
        payload: {
          id: this.resource.id,
        },
      });
      Analytics.getInstance().emitEvent(AdobeAnalyticsEvent.resourceSaveForLater, 'Resource Save for later');
      this.emitFSEvent(AnalyticsEvent.resourceSaveForLater, {
        action: 'Resource Save for later',
      });
      trackTargetEvent('edex-resource-native-share-click');
    } catch (e) {
      if (e.toString().includes('AbortError')) {
        this.track({
          event: AnalyticsEvent.resourceSaveForLaterAborted,
          payload: {
            id: this.resource.id,
          },
        });
        Analytics.getInstance().emitEvent(
          AdobeAnalyticsEvent.resourceSaveForLaterAborted,
          'Resource Save for later aborted'
        );
        this.emitFSEvent(AnalyticsEvent.resourceSaveForLaterAborted, {
          action: 'Resource Save for later aborted',
        });
      }
      return;
    }
  }

  renderSendToDriveProgressDialog() {
    if (!this.isDialogOpen(EdexDomID.resourceSendToDriveProgressDialog)) {
      return null;
    }
    return html`
      <edex-dialog
        id="${EdexDomID.resourceSendToDriveProgressDialog}"
        header=${I18nKey.resourceSendToDriveProgressDialogHeadline}
        open
        .dismissable=${false}
        @close=${this.actions.closeDialog}
      >
        <div slot="content">
          <span>${$t(I18nKey.resourceSendToDriveProgressDialogBody)}</span>
          <p>
            <sp-progress-bar indeterminate small></sp-progress-bar>
          </p>
        </div>
        <footer slot="footer">
          <sp-button-group>
            <sp-button variant="primary" @click=${this.cancelSendToDrive}>${$t(I18nKey.cancel)}</sp-button>
          </sp-button-group>
        </footer>
      </edex-dialog>
    `;
  }

  async handleSave() {
    this.resource.favorite = !this.resource.favorite;
    this.actions.saveResource({ id: this.resource.id });
    this.emitFSEvent(AnalyticsEvent.resourceBookmarkClickedFromToolBar, {
      action: this.resource.favorite ? FsBookmarkAction.unsave : FsBookmarkAction.save,
    });
  }

  async handleBookmarkClick(e) {
    e.preventDefault();
    if (this.isGuest) {
      await EdexAuth.signIn();
    } else {
      this.saveDialogOpen = true;
    }
  }

  async handleUnSave() {
    this.resource.favorite = !this.resource.favorite;
    this.actions.unSaveResource({ id: this.resource.id });
  }

  renderSaveDialog() {
    const { resource, saveDialogOpen } = this;
    if (!saveDialogOpen) {
      return null;
    }
    return html`
      <playlist-save-item-dialog
        .itemID=${this.resource.id}
        .savedChecked="${resource.favorite}"
        @close=${() => {
          this.saveDialogOpen = false;
        }}
        @save="${this.handleSave}"
        @unsave="${this.handleUnSave}"
      ></playlist-save-item-dialog>
    `;
  }

  handleSignUpDialogCTAclick() {
    const dialog = this.downloadResourceDialog;
    this.savePostAuth({ dialog });
    Analytics.getInstance().emitEvent(AdobeAnalyticsEvent.signin, 'Resource download modal window');
    EdexAuth.signIn();
  }

  renderSignInConfirmationDialog() {
    return html`
      <edex-signup-dialog
        .signUpDialogOpen=${this.openSignUpDialog}
        @close=${() => {
          this.openSignUpDialog = false;
        }}
        @handleDialogCTAclick=${this.handleSignUpDialogCTAclick}
      ></edex-signup-dialog>
    `;
  }

  openShareDialog = () => {
    this.track({
      event: AnalyticsEvent.resourceClickShareDialog,
      payload: { id: this.resource.id },
    });
    this.getDialogOpener(EdexDomID.resourceShareDialog)();
  };
  closeExportedDialog(downloadButtonText) {
    if (this.isDialogOpen(EdexDomID.resourceExportedDialog)) {
      this.actions.closeDialog();
    }
    if (downloadButtonText === getTranslation(I18nKey.continue) && !this.isSchoolDistrictDialogNotApplicable()) {
      this.actions.openDialog({
        id: EdexDomID.schoolAttributionDialog,
        payload: {
          showSubjectsAcademics: true,
        },
      });
    }
  }

  renderGetResourceDialog = () => {
    return this.renderDownloadDialog({
      id: EdexDomID.getResourceDialog,
      header: I18nKey.getResource,
    });
  };

  renderGetResourceBtn = () => {
    return html`
      <div class="download-resource-container">
        ${this.renderGetResourceDialog()}
        <sp-button class="download-resource" @click="${this.handleGetResourceClick}">
          ${$t(I18nKey.downloadThisFreeResource)}
        </sp-button>
      </div>
    `;
  };

  renderDownloadAndSaveForLaterBtn = () => {
    const isSmallerResolution = isSmallerMobileScreen();
    return html`
      ${this.renderGetResourceDialog()}
      <div class="m-resource-header-cta-container">
        <sp-button
          size="${isSmallerResolution ? 's' : 'm'}"
          variant="primary"
          treatment="fill"
          class="m-download-resource"
          @click="${this.handleGetResourceClick}"
        >
          ${$t(I18nKey.resourceHeaderDownload)}
        </sp-button>

        <sp-button size="${isSmallerResolution ? 's' : 'm'}" class="m-save-for-later-btn" @click="${this.nativeShare}">
          ${$t(I18nKey.resourceHeaderSaveForLater)}
        </sp-button>
      </div>
    `;
  };

  signUpModal(dialog) {
    if (!this.isGuest) {
      return;
    }
    this.openSignUpDialog = true;
    this.downloadResourceDialog = dialog;
  }

  handleGetResourceClick = async () => {
    const dialog = { id: EdexDomID.getResourceDialog };
    this.signUpModal(dialog);
    this.track({
      event: AnalyticsEvent.getResource,
      payload: {
        id: this.resource.id,
      },
    });
    Analytics.getInstance().emitEvent(AdobeAnalyticsEvent.resourceGet, 'Download this free resource');
    if (this.locationSearch?.includes('fromPersonalisation=true')) {
      Analytics.getInstance().emitEvent(
        AdobeAnalyticsEvent.personalisedResourceDownload,
        'Personalised resource downloaded'
      );
    }
    trackTargetEvent('edex-resource-download-free-resource-click');
    this.actions.openDialog(dialog);
  };

  renderDownloadDialog = ({ id, header, file }: { id: EdexDomID; header: I18nKey; file?: FileSchema }) => {
    if (!this.isDialogOpen(id) || this.isGuest) {
      return null;
    }
    const fileID = file?.id || '';
    return html`
      <edex-dialog-next id="${id}" .header=${$t(header)} .dismissable=${true} open @close=${this.actions.closeDialog}>
        <div class="download-dialog-content" slot="content">
          <edex-download-local
            .resourceID=${this.resource.id}
            .file=${file || this.resource.bundle}
            @success="${file ? null : this.handleDownloadSuccess(ExportType.local)}"
          ></edex-download-local>
          <edex-save-to-gd
            .resourceID=${this.resource.id}
            .fileID=${fileID}
            @success="${this.handleDownloadSuccess(ExportType.googleDrive)}"
          ></edex-save-to-gd>
          <edex-save-to-od
            .resourceID=${this.resource.id}
            .fileID=${fileID}
            @success="${this.handleDownloadSuccess(ExportType.oneDrive)}"
          ></edex-save-to-od>
        </div>
      </edex-dialog-next>
    `;
  };

  handleDownloadSuccess = (exportType: ExportType) => ({
    detail: { link, folderName },
  }: CustomEvent<DownloadPayLoad>) => {
    let downloadSuccessHeader;
    let downloadButtonText;
    let downloadDialogText;
    switch (exportType) {
      case ExportType.local:
        downloadSuccessHeader = getTranslation(I18nKey.resourceDownloadLocal);
        downloadButtonText = this.isSchoolDistrictDialogNotApplicable()
          ? getTranslation(I18nKey.gotIt)
          : getTranslation(I18nKey.continue);
        downloadDialogText = getTranslation(I18nKey.exportLocalDialogText);
        break;
      case ExportType.googleDrive:
        downloadSuccessHeader = getTranslation(I18nKey.resourceDownloadGoogleDrive);
        downloadButtonText = getTranslation(I18nKey.viewGoogleDrive);
        downloadDialogText = getTranslation(I18nKey.exportGoogleDriveDialogText);
        break;
      case ExportType.oneDrive:
        downloadSuccessHeader = getTranslation(I18nKey.resourceDownloadOneDrive);
        downloadButtonText = getTranslation(I18nKey.viewOneDrive);
        downloadDialogText = getTranslation(I18nKey.exportOneDriveDialogText);
        break;
      default:
        break;
    }
    this.actions.openDialog({
      id: EdexDomID.resourceExportedDialog,
      payload: {
        downloadSuccessHeader,
        folderName,
        link,
        downloadButtonText,
        downloadDialogText,
        exportType,
      },
    });
    setSurveyCookie(SURVEY_CONTENT_ACQUIRED_COOKIE_KEY, true, { expires: 2 });
  };

  renderExportedDialog = () => {
    if (!this.isDialogOpen(EdexDomID.resourceExportedDialog)) {
      return null;
    }
    const {
      dialog: {
        payload: { downloadSuccessHeader, link, folderName, downloadButtonText, downloadDialogText, exportType },
      },
    } = this;

    return html`
      <edex-dialog-next
        id="${EdexDomID.resourceExportedDialog}"
        open
        .dismissable=${true}
        .divider=${false}
        .headingClassName=${DialogHeadingClassName.exportDialog}
        .header=${downloadSuccessHeader}
        @close=${this.actions.closeDialog}
      >
        <div class="resource-exported-dialog-content" slot="content">
          <p>
            ${downloadDialogText}
            <br />
            <span>${folderName}</span>
          </p>
        </div>

        <div class="resource-exported-dialog-footer" slot="footer">
          ${this.renderExportedViewButton(link, downloadButtonText, exportType)}
        </div>
      </edex-dialog-next>
    `;
  };

  renderSchoolDistrictDialog = () => {
    if (!this.isDialogOpen(EdexDomID.schoolAttributionDialog)) {
      return null;
    }
    return html`
      <edex-school-attribution-dialog
        ?showSubjectsAcademics=${true}
        ?open-dialog=${true}
        @close=${this.actions.closeDialog}
        @failed=${(event) => {
          errorToast({ message: event.detail.msg });
        }}
      ></edex-school-attribution-dialog>
    `;
  };

  emitDriveViewEvent(exportType: ExportType) {
    const isGd = exportType === ExportType.googleDrive,
      isOd = exportType === ExportType.oneDrive;
    if (isGd || isOd) {
      Analytics.getInstance().emitEvent(
        isGd ? AdobeAnalyticsEvent.resourceViewGoogle : AdobeAnalyticsEvent.resourceViewOneDrive,
        `View in ${isGd ? 'Google' : 'One'} Drive click`
      );
    }
  }

  renderExportedViewButton(link, downloadButtonText, exportType) {
    return link
      ? html`
          <a href=${link} target="_blank" tabindex="-1" class="btn-no-decoration">
            <sp-button variant="cta" class="export-success-btn" @click=${() => this.emitDriveViewEvent(exportType)}>
              ${downloadButtonText}
            </sp-button>
          </a>
        `
      : html`
          <sp-button
            variant="cta"
            class="export-success-btn"
            @click=${() => this.closeExportedDialog(downloadButtonText)}
          >
            ${downloadButtonText}
          </sp-button>
        `;
  }

  get items(): ToolbarItem[] {
    const {
      resource: { favoriteCount, favorite },
    } = this;
    return [
      {
        id: EdexDomID.save,
        tooltip: favorite ? I18nKey.saved : I18nKey.saves,
        additionalToolTip: favorite ? '' : favoriteCount.toLocaleString() + ' ',
        icon: favorite
          ? html`
              <sp-icon-bookmark-single class="fs-resource-page-save"></sp-icon-bookmark-single-outline>
            `
          : html`
              <sp-icon-bookmark-single-outline class="fs-resource-page-save"></sp-icon-bookmark-single-outline>
            `,
        clicked: favorite,
        onClick: this.handleBookmarkClick,
      },
      {
        id: EdexDomID.share,
        tooltip: I18nKey.share,
        icon: html`
          <sp-icon-share-light></sp-icon-share-light>
        `,
        onClick: this.openShareDialog,
        show: !(this.isMobileView && this.isMobileUserAgent),
      },
    ].filter(({ show = true }) => show);
  }

  renderShareDialog = () => {
    if (!this.isDialogOpen(EdexDomID.resourceShareDialog)) {
      return null;
    }
    return html`
      <edex-dialog-next
        id="${EdexDomID.resourceShareDialog}"
        .dismissable=${true}
        .divider=${false}
        class="share-dialog"
        open
        @close="${this.actions.closeDialog}"
      >
        <div class="share-dialog-content" slot="content">
          ${this.shareButtons.map(
            ({ name, shareUrl }) => html`
              <a
                class="share-button"
                href="${shareUrl}"
                target="_blank"
                @click="${(e) => this.handleShare(e, { name, shareUrl })}"
              >
                <img src="${getAssetURL(`${name.toLowerCase()}.svg`)}" alt="${name}" />
              </a>
            `
          )}
        </div>
      </edex-dialog-next>
    `;
  };

  get shareButtons(): ShareButtonMeta[] {
    const {
      resource: { heroImage },
    } = this;
    const link = window.location.href.replace('local.', '');
    const imageUrl = heroImage?.url || `${window.location.origin}${DEFAULT_RESOURCE_IMAGE}`;
    return [
      {
        name: SocialNetwork.Twitter,
        shareUrl: `https://twitter.com/intent/tweet?url=${link}`,
      },
      {
        name: SocialNetwork.Facebook,
        shareUrl: `https://www.facebook.com/sharer/sharer.php?u=${link}`,
      },
      {
        name: SocialNetwork.Pinterest,
        shareUrl: `https://www.pinterest.com/pin/create/button/?url=${link}&media=${imageUrl}`,
      },
      {
        name: SocialNetwork.LinkedIn,
        shareUrl: `https://www.linkedin.com/shareArticle?mini=true&url=${link}`,
      },
    ];
  }
  renderToolbarItem = ({ id, icon, label, onClick, clicked }: ToolbarItem) => {
    return html`
      <div
        data-id="${id}"
        @click="${onClick}"
        class="resource-toolbar-item ${classMap({
          pointer: !!onClick,
          clicked: !!clicked,
          'resource-toolbar-native-share-item': !!this.isMobileUserAgent,
        })}"
      >
        <overlay-trigger placement="top" offset="6">
          <div slot="trigger"><sp-icon>${icon}</sp-icon></div>
        </overlay-trigger>
        <span class="resource-toolbar-label">${$t(label)}</span>
      </div>
    `;
  };

  renderToolbar = () => {
    return html`
      ${this.items.map(this.renderToolbarItem)}
      ${this.isMobileView && this.isMobileUserAgent
        ? this.renderDownloadAndSaveForLaterBtn()
        : this.renderGetResourceBtn()}
      ${this.renderShareDialog()} ${this.renderSignInConfirmationDialog()} ${this.renderExportedDialog()}
      ${this.renderSendToDriveProgressDialog()} ${this.renderSaveDialog()} ${this.renderSchoolDistrictDialog()}
    `;
  };

  render() {
    if (!this.resource) {
      return null;
    }
    return html`
      ${this.isMobileView
        ? html`
            <sp-action-bar variant="fixed" open>
              <div
                class="resource-action-bar-items ${classMap({
                  'resource-action-bar-native-share-items': !!this.isMobileUserAgent,
                })}"
              >
                ${this.renderToolbar()}
              </div>
            </sp-action-bar>
          `
        : html`
            <div class="resource-toolbar">${this.renderToolbar()}</div>
          `}
    `;
  }
}
